/**
 * Direct HTTP client for CREDesktopAgent.exe (localhost:47890–47899).
 * Mirrors extension background.ts and cre-extension-sim/src/agent.js.
 */
import {
  AGENT_CONNECT_BACKOFF_INITIAL_MS,
  AGENT_CONNECT_BACKOFF_MAX_MS,
  AGENT_CONNECT_RETRY_COOLDOWN_MS,
  AGENT_PORT_COUNT,
  AGENT_PORT_START,
  AGENT_PROBE_MS,
  AGENT_SIGNATURE,
} from "./constants";

const PORT_RANGE = Array.from(
  { length: AGENT_PORT_COUNT },
  (_, i) => AGENT_PORT_START + i,
);

let baseUrl = "";
let lastKnownPort = 0;
let connectInFlight = null;
let lastConnectAttemptMs = 0;
let nextConnectAllowedAfter = 0;
let connectBackoffMs = AGENT_CONNECT_BACKOFF_INITIAL_MS;

function resetConnectBackoff() {
  connectBackoffMs = AGENT_CONNECT_BACKOFF_INITIAL_MS;
  nextConnectAllowedAfter = 0;
}

function scheduleConnectBackoff() {
  nextConnectAllowedAfter = Date.now() + connectBackoffMs;
  connectBackoffMs = Math.min(
    connectBackoffMs * 2,
    AGENT_CONNECT_BACKOFF_MAX_MS,
  );
}

async function probePort(port) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AGENT_PROBE_MS);
  try {
    const resp = await fetch(`http://localhost:${port}/`, {
      signal: controller.signal,
    });
    if (!resp.ok) return false;
    const json = await resp.json();
    if (json?.agent === AGENT_SIGNATURE) {
      baseUrl = `http://localhost:${port}`;
      lastKnownPort = port;
      return true;
    }
  } catch {
    /* closed / timed out / blocked */
  } finally {
    clearTimeout(timer);
  }
  return false;
}

function portsForScan(fullScan) {
  if (fullScan) return PORT_RANGE;
  const port = lastKnownPort > 0 ? lastKnownPort : AGENT_PORT_START;
  return [port];
}

/**
 * @param {{ force?: boolean, fullScan?: boolean }} [options]
 * force=true bypasses backoff (user action).
 * fullScan=true tries all ports (boot / settings rediscover); default is one port only.
 */
export async function connect({ force = false, fullScan = false } = {}) {
  if (connectInFlight) return connectInFlight;

  const now = Date.now();
  if (!force) {
    if (baseUrl) return true;
    if (now < nextConnectAllowedAfter) return false;
    if (now - lastConnectAttemptMs < AGENT_CONNECT_RETRY_COOLDOWN_MS) {
      return false;
    }
  }

  connectInFlight = (async () => {
    lastConnectAttemptMs = Date.now();
    baseUrl = "";
    for (const port of portsForScan(fullScan)) {
      if (await probePort(port)) {
        resetConnectBackoff();
        return true;
      }
    }
    scheduleConnectBackoff();
    return false;
  })();

  try {
    return await connectInFlight;
  } finally {
    connectInFlight = null;
  }
}

/**
 * @param {{ force?: boolean, fullScan?: boolean }} [options]
 */
export async function ping({ force = false, fullScan = false } = {}) {
  if (baseUrl) {
    const port = parseInt(baseUrl.split(":").pop() ?? "", 10);
    if (await probePort(port)) return true;
    baseUrl = "";
    if (!fullScan) return false;
  }
  return connect({ force, fullScan });
}

/**
 * @param {Record<string, unknown>} command
 * @param {string|null|undefined} bearerToken
 * @returns {Promise<{ success: boolean, data?: Record<string, unknown>, error?: string }|null>}
 */
export async function sendCommand(command, bearerToken) {
  if (!baseUrl && !(await connect({ force: true, fullScan: false }))) return null;

  try {
    const body = bearerToken ? { ...command, bearer_token: bearerToken } : command;
    const resp = await fetch(`${baseUrl}/command`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      let details = "";
      try {
        details = (await resp.text()).trim();
      } catch {
        /* ignore */
      }
      const suffix = details ? `: ${details.slice(0, 160)}` : "";
      return {
        success: false,
        error: `Agent command failed (HTTP ${resp.status})${suffix}`,
      };
    }

    try {
      return await resp.json();
    } catch {
      return {
        success: false,
        error: "Agent command returned invalid JSON response.",
      };
    }
  } catch {
    return null;
  }
}
